// Component preview data - comprehensive and realistic
export const previewData = {
  navbar: {
    siteName: 'YourBrand',
    logo: {
      mode: 'text',
      text: 'YourBrand',
      textSize: 'large',
      textWeight: 'bold',
      textColor: '#111827',
    },
    links: [
      { text: 'Home', url: '/', order: 0 },
      { text: 'About', url: '/about', order: 1 },
      { text: 'Services', url: '/services', order: 2 },
      { text: 'Portfolio', url: '/portfolio', order: 3 },
      { text: 'Contact', url: '/contact', order: 4 },
    ],
    alignment: 'space-between',
    sticky: true,
  },
  hero: {
    tagline: 'Welcome to the Future',
    title: 'Transform Your Business Today',
    subtitle:
      'Build amazing digital experiences with our powerful platform. Join thousands of companies already growing with us.',
    buttons: [
      { text: 'Start Free Trial', url: '/signup', style: 'primary' },
      { text: 'Watch Demo', url: '/demo', style: 'secondary' },
    ],
    alignment: 'center',
    size: 'large',
  },
  features: {
    title: 'Everything You Need to Succeed',
    features: [
      {
        icon: 'zap',
        title: 'Lightning Fast',
        description:
          'Optimized performance ensures your website loads instantly, keeping visitors engaged and improving SEO.',
      },
      {
        icon: 'shield',
        title: 'Enterprise Security',
        description:
          'Bank-level encryption and security measures protect your data and your customers information.',
      },
      {
        icon: 'trending-up',
        title: 'Analytics & Insights',
        description:
          'Detailed analytics help you understand your audience and make data-driven decisions.',
      },
      {
        icon: 'users',
        title: 'Team Collaboration',
        description:
          'Work together seamlessly with built-in collaboration tools and role-based permissions.',
      },
      {
        icon: 'code',
        title: 'Developer Friendly',
        description:
          'Clean APIs and extensive documentation make customization and integration simple.',
      },
      {
        icon: 'heart',
        title: '24/7 Support',
        description: 'Our dedicated support team is always here to help you succeed, day or night.',
      },
    ],
    layout: 'grid',
    columns: '3',
  },
  cta: {
    title: 'Ready to Get Started?',
    subtitle: 'Join over 10,000 businesses already growing with our platform',
    buttons: [
      { text: 'Start Your Free Trial', url: '/signup', style: 'primary' },
      { text: 'Schedule a Demo', url: '/contact', style: 'secondary' },
    ],
    alignment: 'center',
    style: 'gradient',
  },
  footer: {
    siteName: 'YourBrand',
    description: 'Building the future of digital experiences',
    copyright: '© 2024 YourBrand. All rights reserved.',
    links: [
      { text: 'Features', url: '/features', category: 'Product' },
      { text: 'Pricing', url: '/pricing', category: 'Product' },
      { text: 'About Us', url: '/about', category: 'Company' },
      { text: 'Blog', url: '/blog', category: 'Company' },
      { text: 'Help Center', url: '/help', category: 'Support' },
      { text: 'Contact Us', url: '/contact', category: 'Support' },
      { text: 'Privacy Policy', url: '/privacy', category: 'Legal' },
      { text: 'Terms of Service', url: '/terms', category: 'Legal' },
    ],
    socialLinks: [
      { platform: 'Twitter', url: 'https://twitter.com' },
      { platform: 'LinkedIn', url: 'https://linkedin.com' },
      { platform: 'GitHub', url: 'https://github.com' },
    ],
  },
  form: {
    title: 'Get in Touch',
    description:
      "We'd love to hear from you. Send us a message and we'll respond as soon as possible.",
    fields: [
      {
        identifier: 'firstName',
        label: 'First Name',
        type: 'text',
        required: true,
        placeholder: 'John',
        order: 0,
        validation: {},
      },
      {
        identifier: 'lastName',
        label: 'Last Name',
        type: 'text',
        required: true,
        placeholder: 'Doe',
        order: 1,
        validation: {},
      },
      {
        identifier: 'email',
        label: 'Email Address',
        type: 'email',
        required: true,
        placeholder: 'john@example.com',
        order: 2,
        validation: {},
      },
      {
        identifier: 'phone',
        label: 'Phone Number',
        type: 'tel',
        required: false,
        placeholder: '+1 (555) 123-4567',
        order: 3,
        validation: {},
      },
      {
        identifier: 'company',
        label: 'Company',
        type: 'text',
        required: false,
        placeholder: 'Acme Inc.',
        order: 4,
        validation: {},
      },
      {
        identifier: 'subject',
        label: 'Subject',
        type: 'select',
        required: true,
        options: [
          'General Inquiry',
          'Technical Support',
          'Sales Question',
          'Partnership Opportunity',
        ],
        order: 5,
        validation: {},
      },
      {
        identifier: 'message',
        label: 'Message',
        type: 'textarea',
        required: true,
        placeholder: 'Tell us how we can help...',
        order: 6,
        validation: { minLength: 10 },
      },
    ],
    submitButton: { text: 'Send Message', style: 'primary' },
    emailSettings: {
      fromEmail: 'noreply@yourdomain.com',
      toEmail: 'contact@yourdomain.com',
      subject: 'New Contact Form Submission',
      successMessage: "Thank you for your message! We'll get back to you soon.",
      errorMessage: 'Sorry, something went wrong. Please try again.',
    },
    styling: {
      spacing: 'normal',
      layout: 'single-column',
    },
  },
  pricing: {
    title: 'Simple, Transparent Pricing',
    subtitle: 'Choose the perfect plan for your needs. Always flexible to scale.',
    plans: [
      {
        name: 'Starter',
        description: 'Perfect for individuals and small projects',
        price: { amount: 9, currency: '$', period: 'month' },
        features: [
          '5 Projects',
          '10GB Storage',
          'Basic Analytics',
          'Email Support',
          'SSL Certificate',
        ],
        ctaButton: { text: 'Start Free Trial', url: '/signup?plan=starter', style: 'secondary' },
        highlighted: false,
      },
      {
        name: 'Professional',
        description: 'Best for growing businesses and teams',
        price: { amount: 29, currency: '$', period: 'month', originalAmount: 39 },
        features: [
          'Unlimited Projects',
          '100GB Storage',
          'Advanced Analytics',
          'Priority Support',
          'Custom Domain',
          'Team Collaboration',
          'API Access',
        ],
        ctaButton: { text: 'Start Free Trial', url: '/signup?plan=pro', style: 'primary' },
        highlighted: true,
        highlightLabel: 'Most Popular',
      },
      {
        name: 'Enterprise',
        description: 'Advanced features for large organizations',
        price: { amount: 99, currency: '$', period: 'month' },
        features: [
          'Everything in Pro',
          'Unlimited Storage',
          'Custom Analytics',
          'Dedicated Support',
          'SLA Guarantee',
          'Advanced Security',
          'Custom Integrations',
          'White Labeling',
        ],
        ctaButton: { text: 'Contact Sales', url: '/contact-sales', style: 'secondary' },
        highlighted: false,
      },
    ],
    layout: 'grid',
    columns: '3',
    alignment: 'center',
    cardStyle: 'default',
    showDescription: true,
    showOriginalPrice: true,
    showBadges: true,
    cardShadow: true,
    cardBorder: false,
  },
  team: {
    title: 'Meet Our Leadership Team',
    subtitle: 'Passionate professionals dedicated to your success',
    members: [
      {
        name: 'Sarah Johnson',
        position: 'CEO & Co-Founder',
        imageUrl:
          'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop',
        imageAlt: 'Sarah Johnson',
        bio: 'Former Google executive with 15 years of experience in product innovation.',
        socialLinks: { linkedin: 'https://linkedin.com', twitter: 'https://twitter.com' },
      },
      {
        name: 'Michael Chen',
        position: 'CTO & Co-Founder',
        imageUrl:
          'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop',
        imageAlt: 'Michael Chen',
        bio: 'MIT graduate and former Amazon principal engineer, leading our technical vision.',
        socialLinks: { linkedin: 'https://linkedin.com', website: 'https://github.com' },
      },
      {
        name: 'Emily Rodriguez',
        position: 'VP of Marketing',
        imageUrl:
          'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=400&fit=crop',
        imageAlt: 'Emily Rodriguez',
        bio: 'Growth marketing expert who has scaled startups from 0 to millions in revenue.',
        socialLinks: { linkedin: 'https://linkedin.com', twitter: 'https://twitter.com' },
      },
      {
        name: 'David Kim',
        position: 'VP of Engineering',
        imageUrl:
          'https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=400&h=400&fit=crop',
        imageAlt: 'David Kim',
        bio: 'Former Microsoft architect with expertise in scalable cloud infrastructure.',
        socialLinks: { linkedin: 'https://linkedin.com', website: 'https://github.com' },
      },
    ],
    layout: 'grid',
    columns: '4',
    alignment: 'center',
    cardStyle: 'default',
    showPosition: true,
    showBio: true,
    showSocialLinks: true,
    imageShape: 'circle',
    imageSize: 'medium',
  },
  mediaText: {
    title: 'Built for Modern Businesses',
    subtitle: 'Discover why industry leaders choose our platform',
    content:
      'Our platform combines cutting-edge technology with intuitive design to deliver exceptional results. With powerful features, seamless integrations, and unmatched support, we help businesses of all sizes achieve their digital goals faster than ever before.',
    media: {
      type: 'image',
      url: 'https://images.unsplash.com/photo-1553484771-371a605b060b?w=800&h=600&fit=crop',
      alt: 'Modern business platform',
    },
    layout: 'media-right',
    alignment: 'center',
    spacing: 'normal',
    stackOnMobile: true,
    mobileMediaFirst: true,
    buttons: [
      { text: 'Learn More', url: '/about', style: 'primary', size: 'medium' },
      { text: 'View Case Studies', url: '/case-studies', style: 'secondary', size: 'medium' },
    ],
    mediaWidth: 45,
    textWidth: 55,
    padding: { top: 60, bottom: 60, left: 20, right: 20 },
    titleSize: 'large',
    contentAlignment: 'left',
  },
  stats: {
    title: 'Our Impact in Numbers',
    subtitle: 'Trusted by businesses worldwide',
    layout: 'row',
    animated: false,
    items: [
      { label: 'Happy Clients', value: 500, suffix: '+' },
      { label: 'Projects Completed', value: 1200, suffix: '+' },
      { label: 'Years Experience', value: 15, suffix: '+' },
      { label: 'Team Members', value: 48 },
      { label: 'Countries Served', value: 32, suffix: '+' },
    ],
  },
  textBlocks: {
    componentTitle: 'Why Choose Us',
    componentSubtitle: "We're committed to your success",
    componentDescription: 'Learn more about what drives us and our commitment to excellence.',
    blocks: [
      {
        id: '550e8400-e29b-41d4-a716-446655440001',
        title: 'Our Mission',
        content:
          'To democratize access to powerful digital tools, enabling every business to compete and thrive in the digital economy. We believe that great technology should be accessible, affordable, and easy to use.',
        order: 0,
        titleTag: 'h3',
        contentTag: 'p',
        titleSize: 'xl',
        titleWeight: 'bold',
        contentSize: 'base',
      },
      {
        id: '550e8400-e29b-41d4-a716-446655440002',
        title: 'Our Vision',
        content:
          'We envision a world where every entrepreneur and business has the tools they need to bring their ideas to life, reach their audience, and make a meaningful impact in their communities and beyond.',
        order: 1,
        titleTag: 'h3',
        contentTag: 'p',
        titleSize: 'xl',
        titleWeight: 'bold',
        contentSize: 'base',
      },
      {
        id: '550e8400-e29b-41d4-a716-446655440003',
        title: 'Our Values',
        content:
          "Innovation, integrity, and inclusivity guide everything we do. We're committed to building technology that not only works well but also does good, creating positive outcomes for our users, partners, and society.",
        order: 2,
        titleTag: 'h3',
        contentTag: 'p',
        titleSize: 'xl',
        titleWeight: 'bold',
        contentSize: 'base',
      },
    ],
    layout: 'grid',
    columns: '3',
    gap: 'md',
    globalAlignment: 'center',
  },
};
